import cloneDeep from 'lodash/cloneDeep';
import { getBalanceDueForCheckoutPayment } from '@olo-web/domain/orders/hooks/useCustomPaymentAmount';
import {
  calculateMixedPaymentAmounts,
  type MixedPaymentAmounts,
} from './calculateMixedPaymentAmounts';

export const PAYMENT_EXCEEDS_CHECK_BALANCE = 'Payment amount total cannot exceed the check balance';

const sumTipFromPaymentInfo = (paymentInfo: IOrderPaymentInfo | IOrderPaymentInfo[]): number => {
  const arr = Array.isArray(paymentInfo) ? paymentInfo : [paymentInfo];
  return arr.reduce((sum, p) => sum + parseFloat(String(p.tipAmount ?? 0)), 0);
};

const mergeRecalculatedAmountsIntoPaymentInfo = (
  paymentInfo: IOrderPaymentInfo | IOrderPaymentInfo[],
  amounts: MixedPaymentAmounts
): IOrderPaymentInfo | IOrderPaymentInfo[] => {
  if (Array.isArray(paymentInfo)) {
    if (paymentInfo.length === 2) {
      return [
        { ...paymentInfo[0], amount: amounts.amount, tipAmount: amounts.tipAmount },
        { ...paymentInfo[1], amount: amounts.giftCardAmount, tipAmount: amounts.giftCardTipAmount },
      ];
    }
    return [{ ...paymentInfo[0], amount: amounts.amount, tipAmount: amounts.tipAmount }];
  }
  return {
    ...paymentInfo,
    amount: amounts.amount,
    tipAmount: amounts.tipAmount,
  };
};

const applyKountSessionId = (
  paymentInfo: IOrderPaymentInfo | IOrderPaymentInfo[],
  kountSessionId: string | undefined
) => {
  if (!kountSessionId) return;
  if (Array.isArray(paymentInfo)) {
    paymentInfo.forEach((payment) => {
      if (payment.vtsPaymentInfo) {
        payment.vtsPaymentInfo.transactionSessionId = kountSessionId;
      }
    });
  } else if (paymentInfo.vtsPaymentInfo) {
    paymentInfo.vtsPaymentInfo.transactionSessionId = kountSessionId;
  }
};

/**
 * On "payment exceeds check balance", refetches the order and retries once with amounts
 * derived from the updated balance (same rules as mixed payment at checkout).
 */
export async function addPaymentToCheckWithOverpaymentRetry({
  paymentInfo,
  tryAddPayment,
  refetchOrder,
  guestBalance,
  selectedItemsAmount,
  isSplitByAmount,
  giftCardBalance,
  isStartATabModal,
  merchantDefaultTabAmount,
  kountSessionId,
}: {
  paymentInfo: IOrderPaymentInfo | IOrderPaymentInfo[];
  tryAddPayment: (pi: IOrderPaymentInfo | IOrderPaymentInfo[]) => Promise<IOrder>;
  refetchOrder: () => Promise<{ data?: IOrder }>;
  guestBalance: number | undefined;
  selectedItemsAmount: number | null;
  isSplitByAmount: boolean;
  giftCardBalance: number;
  isStartATabModal: boolean;
  merchantDefaultTabAmount: number;
  kountSessionId: string | undefined;
}): Promise<IOrder> {
  try {
    return await tryAddPayment(paymentInfo);
  } catch (err: unknown) {
    const message =
      err && typeof err === 'object' && 'message' in err
        ? String((err as { message: unknown }).message)
        : '';
    if (message !== PAYMENT_EXCEEDS_CHECK_BALANCE) {
      throw err;
    }

    const refetchResult = await refetchOrder();
    const freshOrder = refetchResult.data;
    if (!freshOrder) {
      throw err;
    }

    const orderBalanceDue =
      getBalanceDueForCheckoutPayment(
        freshOrder,
        { guestBalance, selectedItemsAmount },
        isSplitByAmount
      ) ?? Number(freshOrder.balanceDueAmount);

    if (orderBalanceDue <= 0) {
      throw err;
    }

    const tip = sumTipFromPaymentInfo(paymentInfo);
    const preauthAmount = Math.max(Number(freshOrder.balanceDueAmount), merchantDefaultTabAmount);

    const amounts = calculateMixedPaymentAmounts({
      orderBalanceDue,
      giftCardBalance,
      tip,
      isStartATab: isStartATabModal,
      preauthAmount,
    });

    const adjustedPaymentInfo = mergeRecalculatedAmountsIntoPaymentInfo(
      cloneDeep(paymentInfo),
      amounts
    ) as IOrderPaymentInfo | IOrderPaymentInfo[];

    applyKountSessionId(adjustedPaymentInfo, kountSessionId);

    return await tryAddPayment(adjustedPaymentInfo);
  }
}
